import axios from "axios";
import React, { useEffect, useState } from "react";
import { FaLongArrowAltRight, FaFileExcel, FaSearch } from "react-icons/fa";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const FoodItemSale = () => {

  const [TrOutletId, setOutlet] = useState(null);
  const [foodItemSaleData, setItemSaleData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const apiUrl = process.env.REACT_APP_SERVER_API;

  /* ================= LOAD DEFAULT DATA ================= */

  useEffect(() => {

    const selectedOutlet = JSON.parse(localStorage.getItem("SelectedOutlet"));

    if (selectedOutlet) {
      setOutlet(selectedOutlet);
    }

    const today = new Date().toISOString().split("T")[0];
    setFromDate(today);
    setToDate(today);

  }, []);

  /* ================= FETCH DATA ================= */

  const getFoodItemSale = async () => {

    if (!TrOutletId) return;

    setLoading(true);

    try {

      const resp = await axios.get(
        `${apiUrl}rptItemSale?outletId=${TrOutletId.outlate_id}&fromDate=${fromDate}&toDate=${toDate}&isfood=true`
      );

      const sortedData = resp.data.data.sort(
        (a, b) => b.SoldQty - a.SoldQty
      );

      setItemSaleData(sortedData);

    } catch (error) {

      console.error("Error fetching data:", error);

    }

    setLoading(false);

  };

  /* ================= EXPORT EXCEL ================= */

  const exportToExcel = () => {

    const formattedData = foodItemSaleData.map((item, index) => ({
      Rank: index + 1,
      ItemName: item.item_name,
      SoldQty: item.SoldQty,
      SaleAmount: item.SaleAmt,
      CancelQty: item.CnclQty,
      CompleteQty: item.CmplQty
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "TopFoodItems");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array"
    });

    const data = new Blob([excelBuffer], {
      type: "application/octet-stream"
    });

    saveAs(data, `TopFoodSale_${fromDate}_to_${toDate}.xlsx`);

  };

  /* ================= TOTALS ================= */

  const totalSoldQty = foodItemSaleData.reduce(
    (sum, item) => sum + (item.SoldQty || 0),
    0
  );

  const totalSaleAmt = foodItemSaleData.reduce(
    (sum, item) => sum + (item.SaleAmt || 0),
    0
  );

  return (

    <div className="container-fluid px-2 px-md-3">

      <div className="card shadow-sm border-0 mt-3">

        {/* HEADER */}

        <div className="card-header bg-success text-white d-flex justify-content-between align-items-center flex-wrap">

          <h6 className="mb-0">
            🍽 Top Selling Food Items
          </h6>

          <small>
            {fromDate} → {toDate}
          </small>

        </div>

        <div className="card-body">

          {/* FILTERS */}

          <div className="row g-2 align-items-end mb-3">

            <div className="col-6 col-md-3">
              <label className="small">From Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>

            <div className="col-6 col-md-3">
              <label className="small">To Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>

            <div className="col-6 col-md-2">
              <button
                className="btn btn-success btn-sm w-100"
                onClick={getFoodItemSale}
              >
                <FaSearch /> Show
              </button>
            </div>

            <div className="col-6 col-md-2">
              <button
                className="btn btn-warning btn-sm w-100"
                onClick={exportToExcel}
                disabled={foodItemSaleData.length === 0}
              >
                <FaFileExcel /> Excel
              </button>
            </div>

          </div>

          {/* TABLE */}

          <div className="table-responsive report-table">

            <table className="table table-sm table-hover align-middle">

              <thead className="table-dark sticky-top">

                <tr>
                  <th style={{ width: "60px" }}>#</th>
                  <th>Item</th>
                  <th className="text-end">Qty</th>
                  <th className="text-end">Amount</th>
                  <th className="text-end d-none d-md-table-cell">Cancel</th>
                  <th className="text-end d-none d-md-table-cell">Complete</th>
                </tr>

              </thead>

              <tbody>

                {loading ? (

                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      Loading data...
                    </td>
                  </tr>

                ) : foodItemSaleData.length > 0 ? (

                  foodItemSaleData.map((item, index) => (

                    <tr key={index}>

                      <td>
                        <span className="badge bg-primary">
                          {index + 1}
                        </span>
                      </td>

                      <td className="text-truncate" style={{ maxWidth: "220px" }}>
                        {item.item_name}
                      </td>

                      <td className="text-end fw-bold text-success">
                        {item.SoldQty}
                      </td>

                      <td className="text-end fw-bold">
                        ₹ {(item.SaleAmt || 0).toFixed(2)}
                      </td>

                      <td className="text-end d-none d-md-table-cell">
                        {item.CnclQty}
                      </td>

                      <td className="text-end d-none d-md-table-cell">
                        {item.CmplQty}
                      </td>

                    </tr>

                  ))

                ) : (

                  <tr>
                    <td colSpan="6" className="text-center text-muted py-4">
                      No records found
                    </td>
                  </tr>

                )}

              </tbody>

              {/* TOTAL */}

              <tfoot className="table-light">

                <tr>

                  <td colSpan="2" className="text-end fw-bold">
                    Total <FaLongArrowAltRight />
                  </td>

                  <td className="text-end fw-bold text-success">
                    {totalSoldQty}
                  </td>

                  <td className="text-end fw-bold">
                    ₹ {totalSaleAmt.toFixed(2)}
                  </td>

                  <td></td>
                  <td></td>

                </tr>

              </tfoot>

            </table>

          </div>

        </div>

      </div>

    </div>

  );

};

export default FoodItemSale;